"use client";

import { useEffect, useRef } from "react";
import { usePathname, useSearchParams } from "next/navigation";

import { captureEvent } from "@/lib/analytics";
import { usePostHogClient } from "@/components/PostHogProvider";

/**
 * Reports the outcome of the `/launch` hand-off to the Mac app.
 *
 * `/launch` runs an inline script (`LAUNCH_SCRIPT` in
 * `sections/cardSection/CardSection.tsx`) that fires the `autoscreenshot://`
 * scheme and, if the app doesn't come forward within 2.5s, redirects to
 * `/download`. Without this component both outcomes are invisible: a failed
 * hand-off just looks like ordinary download traffic.
 *
 * The capture deliberately does NOT live in that script. PostHog initialises
 * on idle (up to ~2s, see `PostHogProvider`), which races the 2.5s deadline,
 * and on the fallback path the page unloads before a capture could flush. So
 * the script records the outcome and this component reports it once PostHog is
 * actually available — the same reactive pattern as `PostHogPageView`:
 *
 * - `launch_succeeded`  — the app opened, so `/launch` stays open and can report.
 * - `launch_fell_back`  — the visitor was redirected, so `/download` reports it
 *                         from the `?from=launch&reason=…` marker on the URL.
 *
 * `captureEvent` no-ops when PostHog hasn't loaded or the visitor opted out, so
 * this stays in lock-step with consent without extra gating.
 */
export function LaunchOutcome() {
  const posthog = usePostHogClient();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  // One report per navigation. The effect re-runs when PostHog finishes
  // loading, so without this a ready-then-render sequence double-counts.
  const reportedFor = useRef<string | null>(null);

  useEffect(() => {
    if (!posthog?.capture) return;

    const key = `${pathname}?${searchParams?.toString() ?? ""}`;
    if (reportedFor.current === key) return;

    // Fallback: the redirect carries its own marker, so this fires on
    // /download rather than on the page the visitor already left.
    if (searchParams?.get("from") === "launch") {
      reportedFor.current = key;
      captureEvent("launch_fell_back", {
        reason: searchParams.get("reason") ?? "unknown",
      });
      return;
    }

    const report = () => {
      if (reportedFor.current === key) return;
      reportedFor.current = key;
      captureEvent("launch_succeeded", {});
    };

    // The script may have resolved before PostHog loaded (flag already set), or
    // may resolve after (event). Cover both.
    const w = window as unknown as { __autoScreenshotLaunchOutcome?: string };
    if (w.__autoScreenshotLaunchOutcome === "succeeded") {
      report();
      return;
    }

    document.addEventListener("as:launch-outcome", report);
    return () => document.removeEventListener("as:launch-outcome", report);
  }, [posthog, pathname, searchParams]);

  return null;
}

export default LaunchOutcome;
